<template>
  <div class="article-page">
    <!-- 面包屑导航 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">企业如何做数字化转型</span>
    </nav>

    <!-- 文章主体 -->
    <article class="container">
      <!-- 文章头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">数字化转型</span>
          <span class="tag">战略规划</span>
          <span class="tag">央企国企</span>
        </div>
        <h1 class="article-title">企业如何做数字化转型：从战略规划到落地实施全指南</h1>
        <p class="article-subtitle">系统化方法论 · 五阶段实施路径 · 真实案例解析 · 助力企业构建数字化核心竞争力</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-03-07</span>
          <span><i class="far fa-clock"></i> 阅读约 12 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读卡片 -->
      <section class="intro-card">
        <div class="intro-icon">💡</div>
        <div>
          <h3>导读</h3>
          <p>数字化转型不是简单的技术升级，而是企业战略、组织、流程与技术的全面重构。本文从顶层设计到落地执行，结合国内外标杆企业的实践经验，为决策者提供一套可操作的数字化转型指南。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、什么是数字化转型？</a></li>
          <li><a href="#section2">二、为什么企业必须做数字化转型？</a></li>
          <li><a href="#section3">三、数字化转型的五个阶段</a></li>
          <li><a href="#section4">四、数字化转型的核心要素</a></li>
          <li><a href="#section5">五、行业标杆案例</a></li>
          <li><a href="#section6">六、常见误区与避坑指南</a></li>
          <li><a href="#section7">七、古杉数据的方法与实践</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、什么是数字化转型？</h2>
          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🌐</div>
              <div>
                <h4>数字化转型的本质</h4>
                <p>利用数字技术重塑企业的业务模式、运营流程和客户体验，从而创造新的价值和增长动力。</p>
              </div>
            </div>
          </div>
          <p>数字化转型（Digital Transformation）是指企业通过引入云计算、大数据、人工智能、物联网等新一代信息技术，对战略方向、组织架构、业务流程、运营模式进行系统性变革的过程。</p>
          <p>它并不仅仅是"上一套系统"或"把线下搬到线上"，而是从根本上改变企业创造价值、传递价值和获取价值的方式。</p>

          <div class="comparison-table">
            <h4>数字化转型 vs 信息化 vs 数字化</h4>
            <table>
              <thead>
                <tr>
                  <th>维度</th>
                  <th>信息化</th>
                  <th>数字化</th>
                  <th>数字化转型</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>核心目标</td>
                  <td>流程电子化</td>
                  <td>数据驱动决策</td>
                  <td>商业模式重构</td>
                </tr>
                <tr>
                  <td>技术特征</td>
                  <td>ERP / OA / MES</td>
                  <td>大数据 / 云计算</td>
                  <td>AI / IoT / 数字孪生</td>
                </tr>
                <tr>
                  <td>组织变革</td>
                  <td>IT部门主导</td>
                  <td>业务部门协作</td>
                  <td>全员数字化思维</td>
                </tr>
                <tr>
                  <td>价值衡量</td>
                  <td>效率提升</td>
                  <td>决策优化</td>
                  <td>创新增长</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、为什么企业必须做数字化转型？</h2>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">89%</div>
              <div class="stat-label">的世界500强企业已将数字化转型列为公司级战略</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">2.5x</div>
              <div class="stat-label">数字化领先企业的收入增速是行业平均的2.5倍</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">40%</div>
              <div class="stat-label">的企业因未做数字化转型在过去10年被市场淘汰</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">70%</div>
              <div class="stat-label">的央企国企已制定数字化转型的专项计划</div>
            </div>
          </div>

          <div class="driver-section">
            <h3>推动数字化转型的四大驱动力</h3>
            <div class="driver-grid">
              <div class="driver-card">
                <div class="driver-icon">📋</div>
                <h4>政策驱动</h4>
                <p>国资委明确要求央企国企加快数字化转型，"十四五"数字经济规划、《数据要素×行动计划》等政策密集出台。</p>
              </div>
              <div class="driver-card">
                <div class="driver-icon">⚡</div>
                <h4>竞争驱动</h4>
                <p>数字化原生企业不断侵蚀传统市场，传统企业如不转型将面临降维打击。</p>
              </div>
              <div class="driver-card">
                <div class="driver-icon">👥</div>
                <h4>客户驱动</h4>
                <p>客户体验从线下转向全渠道数字化，个性化、即时化、智能化成为新标准。</p>
              </div>
              <div class="driver-card">
                <div class="driver-icon">🔧</div>
                <h4>技术驱动</h4>
                <p>AI大模型、低代码、云原生等技术成熟度大幅提升，转型成本显著降低。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、数字化转型的五个阶段</h2>
          <p>基于麦肯锡、埃森哲等顶级咨询机构的方法论，结合古杉数据的实践经验，我们将数字化转型归纳为五个阶段：</p>

          <div class="phase-timeline">
            <div class="phase-item" v-for="(phase, index) in phases" :key="index">
              <div class="phase-marker" :style="{ background: phase.color }">{{ index + 1 }}</div>
              <div class="phase-content">
                <h4>{{ phase.title }}</h4>
                <p class="phase-subtitle">{{ phase.subtitle }}</p>
                <ul>
                  <li v-for="item in phase.items" :key="item">{{ item }}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、数字化转型的核心要素</h2>
          <div class="pillar-section">
            <div class="pillar-card" v-for="pillar in pillars" :key="pillar.title">
              <div class="pillar-icon">{{ pillar.icon }}</div>
              <h4>{{ pillar.title }}</h4>
              <p>{{ pillar.desc }}</p>
              <ul>
                <li v-for="point in pillar.points" :key="point">{{ point }}</li>
              </ul>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">🎯</div>
            <div>
              <h4>关键成功因素</h4>
              <p>数字化转型不是"技术问题"，而是"一把手工程"。需要CEO亲自挂帅、CTO/CDO统筹推进，业务与IT深度融合。仅靠IT部门推动的转型项目，成功率不足30%。</p>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、行业标杆案例</h2>
          <div class="case-grid">
            <div class="case-card">
              <div class="case-header energy">
                <span class="case-industry">能源行业</span>
                <h4>某大型能源集团数字化转型</h4>
              </div>
              <div class="case-body">
                <div class="case-challenge">
                  <h5>🔴 挑战</h5>
                  <p>集团下属200+子公司，数据孤岛严重，审计覆盖率不足15%，经营决策依赖Excel手工报表。</p>
                </div>
                <div class="case-solution">
                  <h5>🟢 方案</h5>
                  <p>构建企业级数据中台 + 智能审计平台 + BI报表中心，实现数据统一治理、审计自动化、经营可视化。</p>
                </div>
                <div class="case-result">
                  <h5>🏆 成果</h5>
                  <div class="result-metrics">
                    <span>审计覆盖率 85%↑</span>
                    <span>报表时效 提升10倍</span>
                    <span>数据质量 95%+</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="case-card">
              <div class="case-header finance">
                <span class="case-industry">金融行业</span>
                <h4>某股份制银行全面数字化</h4>
              </div>
              <div class="case-body">
                <div class="case-challenge">
                  <h5>🔴 挑战</h5>
                  <p>线上化率偏低，客户流失严重，风险识别滞后，反欺诈依赖人工审批，效率低下。</p>
                </div>
                <div class="case-solution">
                  <h5>🟢 方案</h5>
                  <p>搭建实时风控引擎 + 客户画像系统 + 智能营销平台，实现千人千面的客户经营和毫秒级风控。</p>
                </div>
                <div class="case-result">
                  <h5>🏆 成果</h5>
                  <div class="result-metrics">
                    <span>线上化率 92%</span>
                    <span>欺诈识别率 99.2%</span>
                    <span>客户满意度 +35%</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="case-card">
              <div class="case-header manufacture">
                <span class="case-industry">制造行业</span>
                <h4>某央企制造集团智能工厂</h4>
              </div>
              <div class="case-body">
                <div class="case-challenge">
                  <h5>🔴 挑战</h5>
                  <p>产线设备分散、故障预警不及时，库存周转慢，质量追溯困难，供应链协同效率低。</p>
                </div>
                <div class="case-solution">
                  <h5>🟢 方案</h5>
                  <p>部署IoT设备感知层 + 数字孪生工厂 + AI质检系统 + 智能供应链协同平台。</p>
                </div>
                <div class="case-result">
                  <h5>🏆 成果</h5>
                  <div class="result-metrics">
                    <span>设备故障率 ↓60%</span>
                    <span>库存周转 +45%</span>
                    <span>质量合格率 99.8%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、常见误区与避坑指南</h2>
          <div class="pitfall-list">
            <div class="pitfall-item" v-for="(pitfall, index) in pitfalls" :key="index">
              <div class="pitfall-header">
                <span class="pitfall-number">误区 {{ index + 1 }}</span>
                <h4>{{ pitfall.mistake }}</h4>
              </div>
              <div class="pitfall-body">
                <div class="wrong">
                  <span class="label">❌ 错误做法</span>
                  <p>{{ pitfall.wrong }}</p>
                </div>
                <div class="right">
                  <span class="label">✅ 正确做法</span>
                  <p>{{ pitfall.right }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、古杉数据的方法与实践</h2>
          <div class="gushan-method">
            <div class="method-intro">
              <p>古杉数据深耕央企国企数字化转型领域，积累了丰富的咨询与落地经验，形成了独有的<strong>"诊断-规划-建设-运营"</strong>四步法方法论。</p>
            </div>
            <div class="method-steps">
              <div class="method-step">
                <div class="step-icon">🔍</div>
                <h4>数字化诊断</h4>
                <p>通过成熟度评估模型，全面诊断企业数字化现状，识别关键差距与优先改进领域。</p>
              </div>
              <div class="method-step">
                <div class="step-icon">📐</div>
                <h4>顶层规划</h4>
                <p>制定3-5年数字化转型蓝图，明确阶段目标、技术架构、组织保障和投资预算。</p>
              </div>
              <div class="method-step">
                <div class="step-icon">🏗️</div>
                <h4>平台建设</h4>
                <p>数据治理平台、智能审计系统、风控引擎、AI智能体平台等产品落地实施。</p>
              </div>
              <div class="method-step">
                <div class="step-icon">🚀</div>
                <h4>持续运营</h4>
                <p>建立数据运营体系、人才培养机制、持续优化闭环，确保转型成果持续创造价值。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>开启您的数字化转型之旅</h2>
        <p>古杉数据专家团队为您提供一对一深度咨询，定制专属转型方案</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约专家咨询</router-link>
          <router-link to="/consulting/digital-transformation" class="btn-outline">了解咨询服务</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'DigitalTransformationGuide',
  data() {
    return {
      phases: [
        {
          title: '战略觉醒期',
          subtitle: '认知重塑 · 战略共识',
          color: '#4A90D9',
          items: [
            '高层达成数字化转型共识',
            '成立数字化转型领导小组',
            '开展数字化成熟度评估',
            '制定数字化愿景与目标'
          ]
        },
        {
          title: '顶层设计期',
          subtitle: '规划蓝图 · 架构设计',
          color: '#1F6FEB',
          items: [
            '制定3-5年数字化转型路线图',
            '设计数据架构与技术架构',
            '明确组织架构与人才规划',
            '制定投资预算与ROI预期'
          ]
        },
        {
          title: '试点突破期',
          subtitle: '聚焦场景 · 快速验证',
          color: '#0E4EAD',
          items: [
            '选择2-3个高价值业务场景',
            '快速构建MVP最小可行产品',
            '验证技术方案与商业价值',
            '总结方法论并形成可复制模式'
          ]
        },
        {
          title: '规模推广期',
          subtitle: '复制成功 · 全面铺开',
          color: '#0A3A82',
          items: [
            '将试点经验推广到更多业务线',
            '建设企业级数据中台',
            '实施全员数字化能力培训',
            '构建数字化运营指标体系'
          ]
        },
        {
          title: '生态融合期',
          subtitle: '创新驱动 · 持续进化',
          color: '#0E1F52',
          items: [
            '打通产业链上下游数据协同',
            '构建数字化创新实验室',
            'AI大模型深度融入业务流程',
            '数据资产化运营与价值变现'
          ]
        }
      ],
      pillars: [
        {
          icon: '🏛️',
          title: '战略引领',
          desc: '数字化转型必须上升为公司级战略',
          points: ['CEO亲自挂帅', '设立CDO/CTO', '纳入绩效考核']
        },
        {
          icon: '📊',
          title: '数据驱动',
          desc: '建立企业级数据治理与资产管理体系',
          points: ['数据标准统一', '主数据管理', '数据质量保障']
        },
        {
          icon: '⚙️',
          title: '技术赋能',
          desc: '构建现代化技术平台底座',
          points: ['云原生架构', '数据中台', 'AI平台']
        },
        {
          icon: '👨‍💼',
          title: '组织变革',
          desc: '建设与数字化匹配的组织能力',
          points: ['敏捷组织', '数字人才', '文化转变']
        },
        {
          icon: '🔄',
          title: '流程重塑',
          desc: '以客户为中心重构端到端流程',
          points: ['流程数字化', '自动化运营', '智能决策']
        },
        {
          icon: '🛡️',
          title: '安全合规',
          desc: '确保数据安全与合规底线',
          points: ['数据安全治理', '隐私保护', '合规审计']
        }
      ],
      pitfalls: [
        {
          mistake: '重技术、轻战略',
          wrong: '直接采购大量系统和平台，追求技术先进性，缺乏清晰的业务目标和价值衡量。',
          right: '先明确转型对业务的价值，以"业务问题"驱动技术选型，建立清晰的ROI评估机制。'
        },
        {
          mistake: '全面铺开、缺乏重点',
          wrong: '同时启动几十个数字化项目，资源分散，结果都做不透。',
          right: '聚焦2-3个高价值场景做深做透，形成标杆后再复制推广。'
        },
        {
          mistake: '忽视数据治理基础',
          wrong: '急于建设AI和大数据应用，但底层数据质量差、标准不统一。',
          right: '数据治理是数字化转型的基石，先建立数据标准、保障数据质量。'
        },
        {
          mistake: '只有IT部门在推动',
          wrong: '数字化转型当作IT项目，业务部门参与度低，沦为"两张皮"。',
          right: '成为"一把手工程"，业务部门深度参与，IT与业务融合共创。'
        },
        {
          mistake: '缺乏持续运营意识',
          wrong: '系统建好就结束，没有运营机制、没有持续优化、没有人才培养。',
          right: '建立数字化运营体系，持续迭代优化，培养数字化人才梯队。'
        }
      ]
    }
  }
}
</script>

<style scoped>
.article-page {
  background: #fff;
  min-height: 100vh;
}

/* 面包屑 */
.breadcrumb-nav {
  max-width: 960px;
  margin: 0 auto;
  padding: 20px 24px;
  font-size: 14px;
  color: #5A5E66;
}
.breadcrumb-nav a {
  color: #1A73E8;
  text-decoration: none;
}
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

/* 容器 */
.container {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px 60px;
}

/* 文章头部 */
.article-header {
  text-align: center;
  padding: 40px 0 32px;
  border-bottom: 1px solid #E8ECF0;
  margin-bottom: 40px;
}
.article-header .article-tags {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
}
.article-header .tag {
  background: #EBF3FE;
  color: #1A73E8;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}
.article-title {
  font-size: 2.2rem;
  font-weight: 800;
  color: #0E1F52;
  line-height: 1.3;
  margin-bottom: 16px;
}
.article-subtitle {
  font-size: 1.1rem;
  color: #5A5E66;
  margin-bottom: 20px;
}
.article-meta {
  display: flex;
  justify-content: center;
  gap: 24px;
  font-size: 14px;
  color: #8C8C8C;
}
.article-meta i { margin-right: 4px; }

/* 导读卡片 */
.intro-card {
  display: flex;
  gap: 16px;
  background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%);
  border-left: 4px solid #1A73E8;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 40px;
}
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

/* 目录 */
.toc {
  background: #F6F8FC;
  border-radius: 12px;
  padding: 24px 28px;
  margin-bottom: 48px;
}
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a {
  color: #1A73E8;
  text-decoration: none;
  font-size: 15px;
  transition: all 0.2s;
}
.toc a:hover { color: #0E1F52; text-decoration: underline; }

/* 正文通用 */
.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 {
  font-size: 1.6rem;
  font-weight: 800;
  color: #0E1F52;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 3px solid #1A73E8;
  display: inline-block;
}
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

/* 视觉块 */
.visual-block {
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
}
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

/* 对比表格 */
.comparison-table {
  margin: 28px 0;
  overflow-x: auto;
}
.comparison-table h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.comparison-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.comparison-table th {
  background: #0E1F52;
  color: #fff;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
}
.comparison-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #E8ECF0;
  color: #3D4150;
}
.comparison-table tr:nth-child(even) td { background: #F6F8FC; }

/* 统计卡片 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin: 28px 0;
}
.stat-card {
  background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  border: 1px solid #D6E4F0;
}
.stat-number {
  font-size: 2.2rem;
  font-weight: 900;
  color: #1A73E8;
  margin-bottom: 8px;
}
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

/* 驱动力 */
.driver-section { margin: 32px 0; }
.driver-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;
}
.driver-card {
  background: #F6F8FC;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #E8ECF0;
  transition: all 0.3s;
}
.driver-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.driver-icon { font-size: 2rem; margin-bottom: 12px; }
.driver-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.driver-card p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 五阶段时间线 */
.phase-timeline { margin: 32px 0; position: relative; padding-left: 40px; }
.phase-timeline::before {
  content: '';
  position: absolute;
  left: 19px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #4A90D9, #0E1F52);
  border-radius: 2px;
}
.phase-item {
  position: relative;
  margin-bottom: 32px;
}
.phase-item:last-child { margin-bottom: 0; }
.phase-marker {
  position: absolute;
  left: -40px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.phase-content {
  background: #F6F8FC;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #E8ECF0;
}
.phase-content h4 { font-size: 18px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.phase-subtitle { font-size: 14px; color: #1A73E8; margin-bottom: 12px !important; font-weight: 500; }
.phase-content ul { list-style: none; padding: 0; margin: 0; }
.phase-content li {
  padding: 4px 0;
  font-size: 14px;
  color: #5A5E66;
  position: relative;
  padding-left: 18px;
}
.phase-content li::before {
  content: '▸';
  position: absolute;
  left: 0;
  color: #1A73E8;
  font-weight: 700;
}

/* 六大支柱 */
.pillar-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 28px 0;
}
.pillar-card {
  background: #F6F8FC;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #E8ECF0;
  transition: all 0.3s;
}
.pillar-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.pillar-icon { font-size: 2rem; margin-bottom: 12px; }
.pillar-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.pillar-card > p { font-size: 14px; color: #5A5E66; margin-bottom: 12px; }
.pillar-card ul { list-style: none; padding: 0; margin: 0; }
.pillar-card li {
  font-size: 13px;
  color: #5A5E66;
  padding: 2px 0 2px 14px;
  position: relative;
}
.pillar-card li::before {
  content: '·';
  position: absolute;
  left: 0;
  color: #1A73E8;
  font-weight: 900;
  font-size: 18px;
  line-height: 1;
}

/* 重点提示 */
.highlight-box {
  display: flex;
  gap: 16px;
  background: #FFF7E6;
  border-left: 4px solid #FAAD14;
  border-radius: 12px;
  padding: 24px;
  margin: 28px 0;
}
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 案例 */
.case-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 24px;
  margin: 28px 0;
}
.case-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E8ECF0;
  transition: all 0.3s;
}
.case-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(31,63,143,0.12); }
.case-header {
  padding: 20px 24px;
  color: #fff;
}
.case-header.energy { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); }
.case-header.finance { background: linear-gradient(135deg, #0D7C66 0%, #094B3E 100%); }
.case-header.manufacture { background: linear-gradient(135deg, #B45309 0%, #78350F 100%); }
.case-industry {
  background: rgba(255,255,255,0.2);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.case-header h4 { font-size: 18px; font-weight: 700; margin-top: 10px; }
.case-body { padding: 24px; }
.case-body h5 { font-size: 14px; font-weight: 700; margin-bottom: 8px; }
.case-body p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 16px; }
.result-metrics {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.result-metrics span {
  background: #EBF3FE;
  color: #1A73E8;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

/* 误区 */
.pitfall-list { margin: 28px 0; }
.pitfall-item {
  background: #F6F8FC;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid #E8ECF0;
}
.pitfall-header {
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #E8ECF0;
}
.pitfall-number {
  background: #FF4D4F;
  color: #fff;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}
.pitfall-header h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin: 0; }
.pitfall-body { padding: 20px 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.pitfall-body .label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}
.pitfall-body .wrong .label { color: #FF4D4F; }
.pitfall-body .right .label { color: #52C41A; }
.pitfall-body p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 古杉方法 */
.gushan-method { margin: 28px 0; }
.method-intro {
  background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
}
.method-intro p { font-size: 16px; color: #3D4150; line-height: 1.7; margin: 0; }
.method-steps {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.method-step {
  background: #F6F8FC;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  border: 1px solid #E8ECF0;
  transition: all 0.3s;
}
.method-step:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.step-icon { font-size: 2rem; margin-bottom: 12px; }
.method-step h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.method-step p { font-size: 13px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* CTA */
.cta-section {
  text-align: center;
  background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%);
  color: #fff;
  border-radius: 16px;
  padding: 48px 32px;
  margin-bottom: 60px;
}
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary {
  background: #1A73E8;
  color: #fff;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s;
}
.btn-primary:hover { background: #1557B0; }
.btn-outline {
  background: transparent;
  color: #fff;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid rgba(255,255,255,0.5);
  transition: all 0.3s;
}
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

/* 响应式 */
@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .driver-grid { grid-template-columns: 1fr; }
  .pillar-section { grid-template-columns: repeat(2, 1fr); }
  .method-steps { grid-template-columns: repeat(2, 1fr); }
  .pitfall-body { grid-template-columns: 1fr; }
  .article-meta { flex-wrap: wrap; justify-content: center; }
  .cta-buttons { flex-direction: column; align-items: center; }
}

@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .pillar-section { grid-template-columns: 1fr; }
  .method-steps { grid-template-columns: 1fr; }
}
</style>
